/* assets/rora-gallery.js — robust + overlay + path/i18n safe + srcset aware */
(() => {
  const DEBUG = true; // 上線可改 false
  const log = (...a) => DEBUG && console.log('[RORA]', ...a);

  // 同一條路徑註冊 raw/解碼/編碼 三種形式（支援中文 handle）
  function pathVariants(p) {
    const raw = String(p || '');
    let dec = raw; try { dec = decodeURIComponent(raw); } catch {}
    let enc = raw; try { enc = encodeURI(dec); } catch {}
    const fmt = (v) => {
      if (v.length > 1 && v.endsWith('/')) v = v.slice(0, -1);
      return v.toLowerCase();
    };
    return Array.from(new Set([fmt(raw), fmt(dec), fmt(enc)]));
  }

  // 從 <img> 蒐集各種來源，去掉 ?v= 並轉小寫
  function urlBucket(img) {
    return [
      img.currentSrc,
      img.src,
      img.getAttribute('src'),
      img.getAttribute('data-src'),
      img.getAttribute('data-image'),
      img.getAttribute('srcset'),
      img.getAttribute('data-srcset'),
    ].filter(Boolean).map(s => String(s).split('?')[0].toLowerCase());
  }

  function matchesAnyTarget(img, targets) {
    const hay = urlBucket(img).join(' ');
    return (targets || []).some(f => hay.includes(String(f).toLowerCase().split('?')[0]));
  }

  // 讀取 config 並建立 Map（每個 path 的多種等價 key 都註冊）
  function loadConfigMap() {
    const list = Array.isArray(window.RORA_GALLERY_CONFIG) ? window.RORA_GALLERY_CONFIG : [];
    const map = new Map();
    for (const row of list) {
      if (!row || !row.path) continue;
      for (const k of pathVariants(row.path)) {
        map.set(k, {
          targets: row.targets || [],
          productNames: row.productNames || [],
          productLinks: row.productLinks || [],
        });
      }
    }
    log('config keys ->', [...map.keys()]);
    return map;
  }

  function pickCurrentKey(map) {
    const vars = pathVariants(location.pathname);
    for (const v of vars) if (map.has(v)) return v;
    return vars[0]; // 只給 log 用
  }

  const processed = new WeakSet();
  const CONFIG_MAP = loadConfigMap();

  function initFor(root) {
    if (!root || processed.has(root)) return;
    if (root.querySelector('.blog-gallery-2')) { processed.add(root); return; }

    const key = pickCurrentKey(CONFIG_MAP);
    const cfg = CONFIG_MAP.get(key);
    if (!cfg) { log('no config for path =', key, '| pathname =', location.pathname); processed.add(root); return; }
    if (!Array.isArray(cfg.targets) || cfg.targets.length === 0) { log('empty targets for', key); processed.add(root); return; }

    // 找到所有匹配 target 的圖片（支援 src/srcset）
    const imgs = [...root.querySelectorAll('img')]
      .filter(img => !img.closest('.blog-gallery-2'))
      .filter(img => matchesAnyTarget(img, cfg.targets));

    log('matched imgs =', imgs.length, imgs.map(i => urlBucket(i)));
    if (imgs.length === 0) { processed.add(root); return; }

    // —— 這行就是 entries 的定義（務必在 forEach 之前存在）——
    const entries = imgs.map(img => {
      const block = img.closest('p, figure, div') || img.parentElement || null;
      const link = img.closest('a');
      return { img, block, link };
    });

    // 錨點與插入父層（先插入空容器，再搬圖，避免 HierarchyError）
    let anchor = entries[0]?.block;
    let insertParent = anchor?.parentNode;
    if (!insertParent) { anchor = null; insertParent = root; }

    const gallery = document.createElement('div');
    gallery.className = 'blog-gallery-2';
    if (anchor) insertParent.insertBefore(gallery, anchor);
    else insertParent.insertBefore(gallery, insertParent.firstChild);

    // 建立每個 item + overlay +（可選）caption
    entries.forEach(({ img, link }, i) => {
      const item = document.createElement('div');
      item.className = 'blog-gallery-2__item';

      // 圖片或包著它的 <a>
      item.appendChild(link && link.contains(img) ? link : img);

      // Hover overlay（漸層遮罩 + 文字 + 整層可點）
      const overlay = document.createElement('div');
      overlay.className = 'hover-overlay';

      const name = (cfg.productNames?.[i] || '').trim();
      const href = (cfg.productLinks?.[i] || '').trim();

      if (href) {
        const a = document.createElement('a');
        a.className = 'overlay-link';
        a.href = href;
        a.target = '_self';
        a.rel = 'noopener';
        a.setAttribute('aria-label', name || 'product');

        const title = document.createElement('span');
        title.className = 'overlay-title';
        title.textContent = name || '';
        a.appendChild(title);
        overlay.appendChild(a);
        overlay.style.pointerEvents = 'auto';
      } else {
        const title = document.createElement('div');
        title.className = 'overlay-title';
        title.textContent = name || '';
        overlay.appendChild(title);
        overlay.style.pointerEvents = 'none';
      }

      item.appendChild(overlay);

      // （可選）下方 caption：若不想同時顯示可刪除這段
      const capName = cfg.productNames?.[i];
      const capHref = cfg.productLinks?.[i];
      if (capName || capHref) {
        const cap = document.createElement(capHref ? 'a' : 'div');
        if (capHref) cap.href = capHref;
        cap.className = 'blog-gallery-2__caption';
        cap.textContent = capName || '';
        item.appendChild(cap);
      }

      gallery.appendChild(item);
    });

    // 清掉被搬空、只剩空白/空 HTML 的段落
    entries.forEach(({ block }) => {
      if (!block || !block.isConnected) return;
      const hasImg = block.querySelector('img');
      const text = block.textContent.replace(/\u00A0/g, ' ').trim();
      const html = block.innerHTML.replace(/\s|&nbsp;|<br\s*\/?>/gi, '').trim();
      if (!hasImg && (text === '' || html === '')) block.remove();
    });

    log('inserted .blog-gallery-2');
    processed.add(root);
  }

  function boot() {
    const roots = document.querySelectorAll('.article-template__content');
    log('roots =', roots.length, '| pathname =', location.pathname, '| picked key =', pickCurrentKey(CONFIG_MAP));
    roots.forEach(initFor);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('turbo:load', boot);
})();
